import React from 'react';
import { Building2, GraduationCap, UserRound, Backpack } from 'lucide-react';
import useReveal, { reveal } from '../../hooks/useReveal';

const ROLES = [
  {
    id: 'administrators',
    icon: Building2,
    title: 'Administrators',
    body: 'The whole school at once: enrolment, finance, staff, admissions and academics.',
  },
  {
    id: 'teachers',
    icon: GraduationCap,
    title: 'Teachers',
    body: 'Their own classes. Registers, scores, remarks and a direct line to the right parents.',
  },
  {
    id: 'parents',
    icon: UserRound,
    title: 'Parents',
    body: 'Their own child. Attendance, reports, bills, receipts and messages from the school.',
  },
  {
    id: 'students',
    icon: Backpack,
    title: 'Students',
    body: 'A student ID card and a wallet their parents fund, for the canteen and the bookstore.',
  },
];

/*
  AtadaBoard is not one admin dashboard: each role signs in to a different view
  of the same school. The rail below is the section's argument, so it is drawn
  as a connected sequence rather than four separate cards.
*/
const Ecosystem = () => {
  const [ref, isVisible] = useReveal();

  return (
    <section id="ecosystem" className="ab-section ab-eco" ref={ref}>
      <div className="ab-shell">
        <header className={`ab-eco__head ${reveal(isVisible)}`}>
          <p className="ab-eyebrow">One platform for the whole school</p>
          <h2 className="ab-h2">Everyone sees the part that belongs to them</h2>
          <p className="ab-lead">
            One record of the school, four ways in. Nobody has to ask someone else for the
            information they need.
          </p>
        </header>

        <ol className="ab-eco__rail">
          {ROLES.map((role, index) => {
            const Icon = role.icon;
            return (
              <li
                key={role.id}
                className={`ab-eco__node ${reveal(isVisible)}`}
                style={{ '--ab-reveal-delay': `${index * 90}ms` }}
              >
                <span className="ab-eco__marker">
                  <Icon size={22} strokeWidth={1.75} />
                </span>
                <h3 className="ab-h3">{role.title}</h3>
                <p className="ab-body">{role.body}</p>
              </li>
            );
          })}
        </ol>
      </div>

      <style jsx>{`
        .ab-eco__head { margin-bottom: clamp(2.5rem, 4vw, 3.5rem); max-width: 46rem; }
        .ab-eco__head .ab-lead { margin-top: 0.875rem; }

        .ab-eco__rail {
          --ab-eco-gap: clamp(1.25rem, 2.5vw, 2.25rem);
          /* Half the marker, used to line the rail up with the marker centres. */
          --ab-eco-half: 23px;
          position: relative;
          list-style: none;
          margin: 0;
          padding: 0;
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: var(--ab-eco-gap);
        }

        /*
          The rail itself: a hairline running behind the markers, tying the four
          roles into one sequence.
        */
        .ab-eco__rail::before {
          content: '';
          position: absolute;
          top: var(--ab-eco-half);
          left: var(--ab-eco-half);
          /* One column width in from the right, less half a marker. */
          right: calc((100% - 3 * var(--ab-eco-gap)) / 4 - var(--ab-eco-half));
          height: 2px;
          background: linear-gradient(
            90deg,
            var(--ab-blue),
            var(--ab-blue) 70%,
            var(--ab-blue-100)
          );
        }

        .ab-eco__node { position: relative; }

        .ab-eco__marker {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 46px;
          height: 46px;
          margin-bottom: 1.25rem;
          border-radius: var(--ab-r-pill);
          background: var(--ab-paper);
          border: 2px solid var(--ab-blue);
          color: var(--ab-blue);
          /* Masks the rail behind the marker so the line meets the circle cleanly. */
          box-shadow: 0 0 0 6px var(--ab-paper);
        }

        .ab-eco__node .ab-h3 { margin-bottom: 0.4375rem; }
        .ab-eco__node .ab-body { font-size: 0.9375rem; }

        @media (max-width: 900px) {
          .ab-eco__rail { grid-template-columns: repeat(2, minmax(0, 1fr)); row-gap: 2.25rem; }
          .ab-eco__rail::before { display: none; }
        }

        @media (max-width: 560px) {
          .ab-eco__rail {
            grid-template-columns: minmax(0, 1fr);
            row-gap: 0;
          }

          /* Single column turns the rail vertical rather than dropping it. */
          .ab-eco__node {
            padding-left: 3.75rem;
            padding-bottom: 2rem;
          }

          .ab-eco__node:last-child { padding-bottom: 0; }

          .ab-eco__node::before {
            content: '';
            position: absolute;
            left: 22px;
            top: 46px;
            bottom: 0;
            width: 2px;
            background: var(--ab-blue-100);
          }

          .ab-eco__node:last-child::before { display: none; }

          .ab-eco__marker {
            position: absolute;
            left: 0;
            top: 0;
            margin-bottom: 0;
            box-shadow: none;
          }
        }
      `}</style>
    </section>
  );
};

export default Ecosystem;
